import { api } from "convex/_generated/api";
import type {
  Id,
  WorkspaceAuditTypeId,
  DepthTier,
  ProfilingAnswers,
} from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";
import { getCurrentLanguage } from "@/packages/utils";

type BackendChecklistSource = "upload" | "official" | "ai_research";
import { useWorkspaceId } from "@/hooks";

interface CreateAuditArgs {
  name: string;
  templateCode?: string;
  templateId?: Id<"auditTemplates">;
  workspaceAuditTypeId?: WorkspaceAuditTypeId;
  description?: string;
  deadline?: number;
  trigger: string;
  tagIds?: Id<"auditTags">[];
  notes?: string;
  auditorRequestText?: string;
  checklistSource?: BackendChecklistSource;
  uploadedDocumentStorageId?: Id<"_storage">;
  sourceStandardId?: Id<"officialStandards">;
  depthTier?: DepthTier;
  profilingAnswers?: ProfilingAnswers;
  piiRole?: "controller" | "processor" | "both" | null;
  orgContext?: {
    physicalPremises: "yes" | "no" | "partial";
    infrastructure: "own-managed" | "cloud-only" | "colocated";
    physicalMedia: boolean;
    secureAreas: boolean;
    workforceModel: "fully-remote" | "hybrid" | "on-site";
  };
}

interface UseCreateAuditReturn {
  createAudit: (args: CreateAuditArgs) => Promise<Id<"audits">>;
  isCreating: boolean;
}

export function useCreateAudit(): UseCreateAuditReturn {
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(api.audit_mutations.createAudit, {
    name: "create-audit",
    silentSuccess: true,
  });

  const createAudit = async (args: CreateAuditArgs): Promise<Id<"audits">> => {
    // useApiMutation unwraps the Result pattern at runtime, returning the inner data.
    // TypeScript can't track this unwrapping through the generic types.
    const result = await mutation.mutateAsync({
      ...args,
      workspaceId,
      locale: getCurrentLanguage(),
    });
    return result;
  };

  return {
    createAudit,
    isCreating: mutation.isPending,
  };
}
